/**
 * AdaptiveDifficultySystem - Adjusts game difficulty based on player performance
 * From PRD Section 5.3
 */

import Phaser from 'phaser';
import type { DifficultyModifiers, PerformanceRecord, LevelScore } from '@/types';

const STORAGE_KEY = 'cellsandlife_difficulty';
const MAX_HISTORY = 10;

export interface AdaptiveDifficultyConfig {
  /** Enable adaptive difficulty */
  enabled?: boolean;
  /** Initial difficulty (0-1) */
  initialDifficulty?: number;
  /** How quickly difficulty adjusts (0-1) */
  adjustmentRate?: number;
}

export interface DifficultyState {
  currentDifficulty: number;
  performanceHistory: PerformanceRecord[];
  totalAttempts: number;
  successfulAttempts: number;
}

/**
 * AdaptiveDifficultySystem dynamically adjusts game challenge
 * based on recent player performance
 */
export class AdaptiveDifficultySystem {
  private scene: Phaser.Scene;
  private config: Required<AdaptiveDifficultyConfig>;
  private state: DifficultyState;

  constructor(scene: Phaser.Scene, config: AdaptiveDifficultyConfig = {}) {
    this.scene = scene;
    this.config = {
      enabled: config.enabled ?? true,
      initialDifficulty: config.initialDifficulty ?? 0.5,
      adjustmentRate: config.adjustmentRate ?? 0.15,
    };

    this.state = this.loadState();
    this.setupEventListeners();
  }

  /**
   * Set up event listeners for performance tracking
   */
  private setupEventListeners(): void {
    this.scene.events.on('level:complete', this.handleLevelComplete, this);
    this.scene.events.on('classification:complete', this.handleClassificationComplete, this);
  }

  /**
   * Handle level completion
   */
  private handleLevelComplete(event: { levelId: string; score: LevelScore }): void {
    const record: PerformanceRecord = {
      levelId: event.levelId,
      score: event.score.total,
      accuracy: event.score.accuracy / 100,
      timeSpent: 0, // Would need to track this separately
      hintsUsed: 0,
      timestamp: new Date(),
    };

    this.recordPerformance(record);
  }

  /**
   * Handle classification attempt
   */
  private handleClassificationComplete(event: { result: { correct: boolean } }): void {
    this.state.totalAttempts++;
    if (event.result.correct) {
      this.state.successfulAttempts++;
    }

    this.adjustDifficulty();
    this.saveState();
  }

  /**
   * Record a performance entry and adjust difficulty
   */
  public recordPerformance(record: PerformanceRecord): void {
    this.state.performanceHistory.push(record);

    // Keep only recent history
    if (this.state.performanceHistory.length > MAX_HISTORY) {
      this.state.performanceHistory = this.state.performanceHistory.slice(-MAX_HISTORY);
    }

    this.adjustDifficulty();
    this.saveState();
  }

  /**
   * Adjust difficulty based on recent performance
   */
  private adjustDifficulty(): void {
    if (!this.config.enabled) return;

    const recentPerformance = this.getRecentPerformance(5);
    const currentDifficulty = this.state.currentDifficulty;

    let targetDifficulty: number;

    if (recentPerformance.score > 0.9) {
      // Doing very well - increase difficulty
      targetDifficulty = Math.min(1.0, currentDifficulty + 0.15);
    } else if (recentPerformance.score > 0.7) {
      // Doing well - slight increase
      targetDifficulty = Math.min(1.0, currentDifficulty + 0.05);
    } else if (recentPerformance.score < 0.4) {
      // Struggling - decrease difficulty
      targetDifficulty = Math.max(0.2, currentDifficulty - 0.15);
    } else if (recentPerformance.score < 0.6) {
      // Some difficulty - slight decrease
      targetDifficulty = Math.max(0.2, currentDifficulty - 0.05);
    } else {
      // In the sweet spot - maintain
      targetDifficulty = currentDifficulty;
    }

    // Smooth adjustment
    this.state.currentDifficulty =
      currentDifficulty + (targetDifficulty - currentDifficulty) * this.config.adjustmentRate;

    // Emit difficulty change event
    this.scene.events.emit('difficulty:adjusted', {
      difficulty: this.state.currentDifficulty,
      performance: recentPerformance,
    });
  }

  /**
   * Get recent performance summary
   */
  public getRecentPerformance(count: number = 5): { score: number; accuracy: number } {
    const recent = this.state.performanceHistory.slice(-count);

    if (recent.length === 0) {
      // Use overall stats if no level history
      if (this.state.totalAttempts > 0) {
        const accuracy = this.state.successfulAttempts / this.state.totalAttempts;
        return { score: accuracy, accuracy };
      }
      return { score: 0.5, accuracy: 0.5 }; // Default to medium
    }

    const avgScore = recent.reduce((sum, r) => sum + r.score, 0) / recent.length / 100;
    const avgAccuracy = recent.reduce((sum, r) => sum + r.accuracy, 0) / recent.length;

    return { score: avgScore, accuracy: avgAccuracy };
  }

  /**
   * Get current difficulty modifiers for a level
   */
  public getDifficultyModifiers(_levelId?: string): DifficultyModifiers {
    const difficulty = this.state.currentDifficulty;
    const performance = this.getRecentPerformance(5);

    return {
      // Specimen difficulty scales with player skill
      specimenDifficulty: 0.4 + difficulty * 0.6,

      // Time pressure - more time for struggling players
      timeMultiplier: difficulty < 0.4 ? 1.5 : difficulty > 0.7 ? 0.8 : 1.0,

      // Hints for struggling players
      hintsEnabled: difficulty < 0.5 || performance.accuracy < 0.6,

      // Hint cooldown - shorter for struggling players
      hintCooldown: difficulty < 0.4 ? 10 : difficulty > 0.7 ? 30 : 20,

      // Evidence required - more for advanced players
      evidenceRequired: difficulty > 0.8 ? 5 : 3,

      // Distractor similarity - more confusing for advanced players
      distractorSimilarity: 0.3 + difficulty * 0.6,
    };
  }

  /**
   * Get current difficulty level (0-1)
   */
  public getCurrentDifficulty(): number {
    return this.state.currentDifficulty;
  }

  /**
   * Get difficulty as a label
   */
  public getDifficultyLabel(): string {
    const d = this.state.currentDifficulty;
    if (d < 0.3) return 'Beginner';
    if (d < 0.5) return 'Easy';
    if (d < 0.7) return 'Normal';
    if (d < 0.85) return 'Challenging';
    return 'Expert';
  }

  /**
   * Manually set difficulty (for settings menu)
   */
  public setDifficulty(level: number): void {
    this.state.currentDifficulty = Math.max(0.1, Math.min(1.0, level));
    this.saveState();

    this.scene.events.emit('difficulty:set', {
      difficulty: this.state.currentDifficulty,
      label: this.getDifficultyLabel(),
    });
  }

  /**
   * Enable/disable adaptive difficulty
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    this.saveState();
  }

  /**
   * Check if adaptive difficulty is enabled
   */
  public isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Get performance statistics
   */
  public getStats(): {
    totalAttempts: number;
    successRate: number;
    currentDifficulty: number;
    difficultyLabel: string;
  } {
    return {
      totalAttempts: this.state.totalAttempts,
      successRate:
        this.state.totalAttempts > 0
          ? this.state.successfulAttempts / this.state.totalAttempts
          : 0,
      currentDifficulty: this.state.currentDifficulty,
      difficultyLabel: this.getDifficultyLabel(),
    };
  }

  /**
   * Load state from localStorage
   */
  private loadState(): DifficultyState {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          currentDifficulty: parsed.currentDifficulty ?? this.config.initialDifficulty,
          performanceHistory: (parsed.performanceHistory || []).map(
            (r: PerformanceRecord & { timestamp: string }) => ({
              ...r,
              timestamp: new Date(r.timestamp),
            })
          ),
          totalAttempts: parsed.totalAttempts || 0,
          successfulAttempts: parsed.successfulAttempts || 0,
        };
      }
    } catch (e) {
      console.warn('Failed to load difficulty state:', e);
    }

    return {
      currentDifficulty: this.config.initialDifficulty,
      performanceHistory: [],
      totalAttempts: 0,
      successfulAttempts: 0,
    };
  }

  /**
   * Save state to localStorage
   */
  private saveState(): void {
    try {
      const serializable = {
        currentDifficulty: this.state.currentDifficulty,
        performanceHistory: this.state.performanceHistory.map((r) => ({
          ...r,
          timestamp: r.timestamp.toISOString(),
        })),
        totalAttempts: this.state.totalAttempts,
        successfulAttempts: this.state.successfulAttempts,
        enabled: this.config.enabled,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
    } catch (e) {
      console.warn('Failed to save difficulty state:', e);
    }
  }

  /**
   * Reset difficulty to initial state
   */
  public reset(): void {
    this.state = {
      currentDifficulty: this.config.initialDifficulty,
      performanceHistory: [],
      totalAttempts: 0,
      successfulAttempts: 0,
    };
    localStorage.removeItem(STORAGE_KEY);

    this.scene.events.emit('difficulty:reset', {
      difficulty: this.state.currentDifficulty,
    });
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.saveState();

    this.scene.events.off('level:complete', this.handleLevelComplete, this);
    this.scene.events.off('classification:complete', this.handleClassificationComplete, this);
  }
}
